import React, { useEffect } from 'react';
import {
  FormControl,
  FormLabel,
  VStack,
  InputGroup,
  Input,
  InputLeftAddon,
  FormHelperText,
  Link,
  InputRightAddon,
} from '@chakra-ui/react';
import * as _ from 'lodash';
import DollarSignIcon from '../DollarSignIcon';
import PercentIcon from '../PercentIcon';

const ADDITIONAL_DELIVERABLES_LINK =
  'https://docs.google.com/spreadsheets/d/1cm_u5w9ASITI-jh7zIPr-XiM9u445qOL1yd1027LIb8/edit#gid=448947046';

const MARKUP_PRECISION = 1;

export const formatManualMarkupForDisplay = (manualMarkup: number): number => {
  let formatted = manualMarkup;
  if (formatted === 1) {
    formatted = 0;
  } else {
    formatted = (formatted - 1) * 100;
  }
  formatted = Number.parseFloat(formatted.toFixed(MARKUP_PRECISION));
  return formatted;
};

export const formatManualMarkupForSaving = (
  manualMarkup: string | number,
): number => {
  let markup =
    typeof manualMarkup === 'string' ? _.toNumber(manualMarkup) : manualMarkup;
  markup = markup === 0 ? 1 : 1 + markup / 100;
  // precision preserved with + 2 since we divide markup by 100
  // 12.34% -> 1.1234 -> 1.123
  markup = Number.parseFloat(markup.toFixed(MARKUP_PRECISION + 2));
  return markup;
};

function FeeTypeSection({
  markupOrDiscountPercentage,
  setMarkupOrDiscountPercentage,
  absoluteMarkupOrDiscount,
  setAbsoluteMarkupOrDiscount,
}: {
  markupOrDiscountPercentage: number;
  absoluteMarkupOrDiscount: number;
  setMarkupOrDiscountPercentage: (markupOrDiscountPercentage: number) => void;
  setAbsoluteMarkupOrDiscount: (absoluteMarkupOrDiscount: number) => void;
}) {
  const [manualMarkupPct, setManualMarkupPct] = React.useState<number | string>(
    formatManualMarkupForDisplay(markupOrDiscountPercentage),
  );
  const [manualMarkupAbs, setManualMarkupAbs] = React.useState<number | string>(
    absoluteMarkupOrDiscount,
  );

  useEffect(() => {
    setMarkupOrDiscountPercentage(formatManualMarkupForSaving(manualMarkupPct));
  }, [manualMarkupPct]);

  useEffect(() => {
    setAbsoluteMarkupOrDiscount(_.toNumber(manualMarkupAbs));
  }, [manualMarkupAbs]);

  return (
    <VStack spacing="2%">
      <FormControl textAlign="start">
        <FormLabel>Mark-up (+) / Discount (-) Percentage</FormLabel>
        <InputGroup>
          <Input
            type="number"
            value={manualMarkupPct}
            onChange={(e) =>
              setManualMarkupPct(
                e.target.value ? _.toNumber(e.target.value) : '',
              )
            }
            onBlur={(e) => {
              setManualMarkupPct(_.toNumber(e.target.value));
            }}
            isInvalid={!_.inRange(_.toNumber(manualMarkupPct), -99, 100)}
          />
          <InputRightAddon pointerEvents="none">
            <PercentIcon />
          </InputRightAddon>
        </InputGroup>
        <FormHelperText>
          % (eg. 20 or 30), NOT counting markups/discounts from choices already
          on this page (ex: creative testing). Use a negative (eg. -10)
          for a discount.
        </FormHelperText>
      </FormControl>

      <FormControl textAlign="start">
        <FormLabel>
          Additional fee/discount(s) to add to the total price
        </FormLabel>
        <InputGroup>
          <InputLeftAddon>
            <DollarSignIcon />
          </InputLeftAddon>
          <Input
            type="number"
            value={manualMarkupAbs}
            onChange={(e) =>
              setManualMarkupAbs(
                e.target.value ? _.toNumber(e.target.value) : '',
              )
            }
            onBlur={(e) => {
              setManualMarkupAbs(_.round(_.toNumber(e.target.value), 2));
            }}
          />
        </InputGroup>
        <FormHelperText>
          <Link
            href={ADDITIONAL_DELIVERABLES_LINK}
            color="brand.linkColor"
            isExternal
          >
            Additional Deliverable Prices
          </Link>
        </FormHelperText>
      </FormControl>
    </VStack>
  );
}

export default FeeTypeSection;
